import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SubTabs from "@/components/SubTabs";
import { api } from "../lib/api";
import { useRole } from "../context/RoleContext";
import { useAuth } from "@/lib/AuthContext";
import DeliveryWorkspace from "../components/DeliveryWorkspace";
import DeliveriesTab from "../components/DeliveriesTab";
import PullQueue from "../components/PullQueue";
import PullScreen from "../components/PullScreen";
import PullDesk from "../components/PullDesk";
import TicketsRegister from "../components/TicketsRegister";
import ReceivingTab from "../components/ReceivingTab";

const TABS = [["tickets", "Tickets"], ["pull", "Pull"], ["deliveries", "Deliveries"], ["receiving", "Receiving"]];
const PICKER_ROLES = ["warehouse"];

/** WH-SIMPLE-1 — four tabs, one job each. Landing by ROLES-1: picker → Pull, everyone else → Tickets. */
export default function WarehousePage() {
  const { role } = useRole();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const routerNav = useNavigate();
  const actor = user?.email || user?.name || "Warehouse";
  const isPicker = PICKER_ROLES.includes(String(role || "").toLowerCase());
  const deliveryId = searchParams.get("delivery") || "";
  const dlvId = searchParams.get("dlv") || "";
  const pullId = searchParams.get("pull") || "";
  const ticketId = searchParams.get("ticket") || "";
  const lineKey = searchParams.get("line") || "";
  const week = searchParams.get("week") || "";
  const day = searchParams.get("day") || "";
  const rawView = searchParams.get("view") || "";
  const [wide, setWide] = useState(() => window.innerWidth >= 1024);
  useEffect(() => { const f = () => setWide(window.innerWidth >= 1024); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);

  // Redirects for the removed views — nothing lost: Details / work mode → Pull (picker) or the Tickets drawer; Material status → Tickets with Issues on
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    let changed = false;
    if (rawView === "status") { p.set("view", "tickets"); p.set("issues", "1"); changed = true; }
    if (rawView === "pickups") { p.set("view", "receiving"); changed = true; }
    const legacyTicket = p.get("work") || (rawView === "details" ? p.get("ticket") : "");
    if (p.has("work") || rawView === "details") {
      p.delete("work");
      if (isPicker) { p.set("view", "pull"); if (legacyTicket) p.set("pull", legacyTicket); }
      else { p.set("view", "tickets"); if (legacyTicket) p.set("ticket", legacyTicket); }
      changed = true;
    }
    if (changed) routerNav({ search: `?${p.toString()}` }, { replace: true });
  }, [rawView]);        // eslint-disable-line react-hooks/exhaustive-deps

  const view = deliveryId ? "delivery" : (rawView && TABS.some(([k]) => k === rawView) ? rawView : (isPicker ? "pull" : "tickets"));
  const setUrl = (patch, replace = false) => {
    const p = new URLSearchParams(window.location.search);
    Object.entries(patch).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    const q = p.toString() ? `?${p.toString()}` : "";
    if (q !== window.location.search) routerNav({ search: q }, { replace });
  };
  const goView = (k) => setUrl({ view: k, delivery: "", dlv: "", pull: "", ticket: "", line: "" });

  const [deliverFor, setDeliverFor] = useState(null);
  const [deliveryRefresh, setDeliveryRefresh] = useState(0);
  const [installers, setInstallers] = useState([]);
  useEffect(() => { api.get("/installers").then(({ data }) => setInstallers(Array.isArray(data) ? data : data?.items || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!deliveryId) { if (deliverFor) { setDeliverFor(null); setDeliveryRefresh((n) => n + 1); } return; }
    if (deliverFor?.id === deliveryId) return;
    api.get(`/delivery/tickets/${deliveryId}`).then(({ data }) => setDeliverFor({ id: deliveryId, ticket_number: data.ticket?.ticket_number })).catch(() => goView("deliveries"));
  }, [deliveryId]);     // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.body.classList.toggle("frs-field-expanded", (view === "pull" && !!pullId && !wide) || (view === "tickets" && !!ticketId && !wide) || (view === "deliveries" && !!dlvId && !wide));
    return () => document.body.classList.remove("frs-field-expanded");
  }, [view, pullId, ticketId, dlvId, wide]);
  const phoneFull = (view === "pull" && pullId && !wide) || (view === "tickets" && ticketId && !wide) || (view === "deliveries" && dlvId && !wide) || view === "delivery";

  return (
    <div className="wh-house space-y-4 px-2 sm:px-4 pb-4" data-testid="warehouse-page" data-view={view}>
      <div className={`${phoneFull ? "hidden sm:block" : "block"} sticky top-0 z-30 -mx-2 sm:-mx-4 bg-background`}>
        <SubTabs testId="warehouse-views" active={view === "delivery" ? "deliveries" : view}
                 tabs={TABS.map(([k, label]) => ({ key: k, label, testId: `warehouse-view-${k}` }))} onChange={goView} />
      </div>

      {view === "pull" && wide && (
        <PullDesk week={week} day={day} pull={pullId} line={lineKey} actor={actor}
                  onWeek={(w) => setUrl({ week: w, day: day === "week" ? "week" : "", pull: "" })} onDay={(d) => setUrl({ day: d }, true)} onJump={(d) => setUrl({ week: d, day: d, pull: "" })}
                  onPull={(id) => setUrl({ pull: id, line: "" })} onChanged={() => setDeliveryRefresh((n) => n + 1)} />
      )}
      {view === "pull" && !wide && !pullId && <PullQueue week={week} day={day} onWeek={(w) => setUrl({ week: w, day: day === "week" ? "week" : "", pull: "" })} onDay={(d) => setUrl({ day: d }, true)} onOpen={(c) => setUrl({ pull: c.id })} />}
      {view === "pull" && !wide && pullId && <PullScreen ticketId={pullId} actor={actor} highlightKey={lineKey} onBack={() => setUrl({ pull: "", line: "" })} onChanged={() => setDeliveryRefresh((n) => n + 1)} />}

      {view === "tickets" && (
        <TicketsRegister week={week} day={day} ticketId={ticketId} wide={wide} actor={actor} role={String(role || "").toLowerCase()}
                         issuesOn={searchParams.get("issues") === "1"}
                         onWeek={(w) => setUrl({ week: w, day: day === "week" ? "week" : "", ticket: "" })} onDay={(d) => setUrl({ day: d }, true)} onTicket={(id) => setUrl({ ticket: id })} />
      )}

      {view === "deliveries" && <DeliveriesTab refreshKey={deliveryRefresh} dlvId={dlvId} wide={wide} actor={actor} onOpen={(id) => setUrl({ dlv: id })} />}
      {view === "delivery" && deliverFor && (
        <DeliveryWorkspace ticketId={deliverFor.id} ticketNumber={deliverFor.ticket_number} actor={actor} installers={installers}
                           onClose={() => goView("deliveries")} onChanged={() => setDeliveryRefresh((n) => n + 1)} />
      )}

      {view === "receiving" && <ReceivingTab actor={actor} installers={installers} canPull={["admin", "management", "warehouse", "warehouse_lead"].includes(String(role || "").toLowerCase())} />}
    </div>
  );
}
